/**
 * Side-bet registry. Each entry is a pure evaluator over the opening deal:
 * the player's first two cards plus the dealer's upcard. They settle straight
 * after the deal, before anyone acts, and never touch the main wager.
 *
 * To add a side bet, write a `SideBetDef` and register it in `SIDE_BETS`.
 */
import { handValue } from './blackjack.js';
import type { Card, Rank, SideBetId, Suit } from './types.js';

/** The opening deal, which is all any side bet is allowed to see. */
export interface SideBetContext {
  /** The player's first two cards, before any hit, double or split. */
  playerCards: readonly Card[];
  dealerUpcard: Card;
}

export interface SideBetHit {
  /** The winning combination's display name, e.g. "Suited trips". */
  label: string;
  /**
   * The ":1" side of the odds; a 25:1 win has a multiplier of 25. Zero is a
   * push: the stake comes back and nothing else.
   */
  multiplier: number;
}

export interface SideBetDef {
  id: SideBetId;
  name: string;
  blurb: string;
  /** Paytable rows for the UI, best first. */
  paytable: readonly { label: string; odds: string }[];
  /** Returns the winning row, or null when the bet loses. */
  evaluate: (ctx: SideBetContext) => SideBetHit | null;
}

const RED: Suit[] = ['H', 'D'];
const isRed = (suit: Suit) => RED.includes(suit);
const sameColour = (a: Suit, b: Suit) => isRed(a) === isRed(b);

/** Straight order, ace low. `straightRanks` also tries the ace high. */
const RANK_ORDER: Rank[] = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
const rankIndex = (rank: Rank) => RANK_ORDER.indexOf(rank) + 1;

const perfectPairs: SideBetDef = {
  id: 'perfect_pairs',
  name: 'Perfect Pairs',
  blurb: "Pays when your first two cards are a pair. The tighter the match, the bigger the pay.",
  paytable: [
    { label: 'Perfect pair', odds: '25:1' },
    { label: 'Coloured pair', odds: '12:1' },
    { label: 'Mixed pair', odds: '6:1' },
  ],
  evaluate: ({ playerCards }) => {
    const [a, b] = playerCards;
    if (!a || !b || a.rank !== b.rank) return null;
    if (a.suit === b.suit) return { label: 'Perfect pair', multiplier: 25 };
    if (sameColour(a.suit, b.suit)) return { label: 'Coloured pair', multiplier: 12 };
    return { label: 'Mixed pair', multiplier: 6 };
  },
};

/** True for three consecutive ranks, counting the ace either low or high. */
function isStraight(ranks: Rank[]): boolean {
  const low = ranks.map(rankIndex).sort((x, y) => x - y);
  const consecutive = (v: number[]) => v[1] === v[0]! + 1 && v[2] === v[1]! + 1;
  if (consecutive(low)) return true;
  // Ace high: Q-K-A reads as 12-13-14.
  const high = ranks.map((r) => (r === 'A' ? 14 : rankIndex(r))).sort((x, y) => x - y);
  return consecutive(high);
}

const twentyOnePlusThree: SideBetDef = {
  id: 'twenty_one_plus_three',
  name: '21+3',
  blurb: 'Your first two cards plus the dealer upcard, scored as a three-card poker hand.',
  paytable: [
    { label: 'Suited trips', odds: '100:1' },
    { label: 'Straight flush', odds: '40:1' },
    { label: 'Three of a kind', odds: '30:1' },
    { label: 'Straight', odds: '10:1' },
    { label: 'Flush', odds: '5:1' },
  ],
  evaluate: ({ playerCards, dealerUpcard }) => {
    const cards = [...playerCards.slice(0, 2), dealerUpcard];
    if (cards.length < 3) return null;
    const ranks = cards.map((c) => c.rank);
    const suits = cards.map((c) => c.suit);
    const trips = ranks[0] === ranks[1] && ranks[1] === ranks[2];
    const flush = suits[0] === suits[1] && suits[1] === suits[2];
    const straight = isStraight(ranks);

    if (trips && flush) return { label: 'Suited trips', multiplier: 100 };
    if (straight && flush) return { label: 'Straight flush', multiplier: 40 };
    if (trips) return { label: 'Three of a kind', multiplier: 30 };
    if (straight) return { label: 'Straight', multiplier: 10 };
    if (flush) return { label: 'Flush', multiplier: 5 };
    return null;
  },
};

const luckyLucky: SideBetDef = {
  id: 'lucky_lucky',
  name: 'Lucky Lucky',
  blurb: 'Your two cards plus the dealer upcard: totals of 19 to 21 pay, and three sevens pay huge.',
  paytable: [
    { label: 'Suited 7-7-7', odds: '200:1' },
    { label: '7-7-7', odds: '100:1' },
    { label: 'Suited 21', odds: '15:1' },
    { label: '21', odds: '3:1' },
    { label: '20', odds: '2:1' },
    { label: '19', odds: '2:1' },
  ],
  evaluate: ({ playerCards, dealerUpcard }) => {
    const cards = [...playerCards.slice(0, 2), dealerUpcard];
    if (cards.length < 3) return null;
    const suited = cards.every((c) => c.suit === cards[0]!.suit);
    if (cards.every((c) => c.rank === '7')) {
      return suited
        ? { label: 'Suited 7-7-7', multiplier: 200 }
        : { label: '7-7-7', multiplier: 100 };
    }
    const total = handValue(cards).total;
    if (suited && total === 21) return { label: 'Suited 21', multiplier: 15 };
    if (total === 21) return { label: '21', multiplier: 3 };
    if (total === 20) return { label: '20', multiplier: 2 };
    if (total === 19) return { label: '19', multiplier: 2 };
    return null;
  },
};

const suitEmUp: SideBetDef = {
  id: 'suit_em_up',
  name: "Suit 'Em Up",
  blurb: "Your first two cards share a suit. Suited aces pay best of all.",
  paytable: [
    { label: 'Suited aces', odds: '60:1' },
    { label: 'Same suit', odds: '10:1' },
  ],
  evaluate: ({ playerCards }) => {
    const [a, b] = playerCards;
    if (!a || !b || a.suit !== b.suit) return null;
    if (a.rank === 'A' && b.rank === 'A') return { label: 'Suited aces', multiplier: 60 };
    return { label: 'Same suit', multiplier: 10 };
  },
};

const royalMatch: SideBetDef = {
  id: 'royal_match',
  name: 'Royal Match',
  blurb: 'Your first two cards are suited, with a jackpot for the king and queen of one suit.',
  paytable: [
    { label: 'Royal match (K-Q suited)', odds: '25:1' },
    { label: 'Any two suited', odds: '3:1' },
  ],
  evaluate: ({ playerCards }) => {
    const [a, b] = playerCards;
    if (!a || !b || a.suit !== b.suit) return null;
    const royal = (a.rank === 'K' || a.rank === 'Q') && a.rank !== b.rank;
    if (royal) return { label: 'Royal match (K-Q suited)', multiplier: 25 };
    return { label: 'Any two suited', multiplier: 3 };
  },
};

/**
 * Over/Under 13 reads the first two cards with aces at one and faces at ten.
 * The two bets are mirror images: totals above (below) 13 win even money and
 * exactly 13 pushes the stake back.
 */
const lowTotal = (cards: readonly Card[]): number =>
  cards.slice(0, 2).reduce((sum, c) => sum + Math.min(rankIndex(c.rank), 10), 0);

const over13: SideBetDef = {
  id: 'over_13',
  name: 'Over 13',
  blurb: 'Your first two cards total more than 13, aces counting one. Exactly 13 pushes.',
  paytable: [{ label: '14 or more', odds: '1:1' }],
  evaluate: ({ playerCards }) => {
    if (playerCards.length < 2) return null;
    const total = lowTotal(playerCards);
    if (total > 13) return { label: 'Over 13', multiplier: 1 };
    if (total === 13) return { label: 'Push (13)', multiplier: 0 };
    return null;
  },
};

const under13: SideBetDef = {
  id: 'under_13',
  name: 'Under 13',
  blurb: 'Your first two cards total less than 13, aces counting one. Exactly 13 pushes.',
  paytable: [{ label: '12 or less', odds: '1:1' }],
  evaluate: ({ playerCards }) => {
    if (playerCards.length < 2) return null;
    const total = lowTotal(playerCards);
    if (total < 13) return { label: 'Under 13', multiplier: 1 };
    if (total === 13) return { label: 'Push (13)', multiplier: 0 };
    return null;
  },
};

export const SIDE_BETS: Record<SideBetId, SideBetDef> = {
  perfect_pairs: perfectPairs,
  twenty_one_plus_three: twentyOnePlusThree,
  lucky_lucky: luckyLucky,
  suit_em_up: suitEmUp,
  royal_match: royalMatch,
  over_13: over13,
  under_13: under13,
};

export const sideBetDef = (id: SideBetId): SideBetDef | undefined => SIDE_BETS[id];

export interface SideBetSettlement {
  id: SideBetId;
  stake: number;
  /** The winning combination, or null on a loss. */
  label: string | null;
  /** Chips returned, stake included; 0 on a loss. */
  payout: number;
}

/**
 * Settles one side bet against the opening deal. A zero-multiplier hit is a
 * push: the stake returns untouched and the label says why.
 */
export function settleSideBet(
  id: SideBetId,
  stake: number,
  ctx: SideBetContext,
): SideBetSettlement {
  const def = SIDE_BETS[id];
  const hit = def ? def.evaluate(ctx) : null;
  if (!hit) return { id, stake, label: null, payout: 0 };
  return { id, stake, label: hit.label, payout: stake + stake * hit.multiplier };
}
